import { lazy, Suspense, useCallback, useEffect, useRef } from 'react';
import { Global } from '@emotion/react';
import { v4 as uuidv4 } from 'uuid';

import {
  bridgeAtom,
  errorAtom,
  isOpenChatAtom,
  messagesAtom,
  messageTextAtom,
  useAtom,
  useSetValue,
  useValue,
} from '@/store';

import { addUserScalableNo, AppEvents, getProductIdType, removeUserScalable } from '@/utils';

import { viewHistory } from '@/modules/api';
import { useEvent, useTheme } from '@/modules/common';
import { ErrorType, MessageReasonType, MessageSenderType, MessageType, SendToModelStatusType } from '@/modules/models';
import { FontLoader } from '@/modules/saywhatt-chat/components';
import { Widget } from '@/modules/widget';

import { globalStyles } from './SaywhattChat.styles';

const Content = lazy(() => import('./components/content').then((module) => ({ default: module.Content })));

export type SaywhattChatProps = {
  onSendMessage(message: MessageType): Promise<{ error: ErrorType } | void>;
};

export const SaywhattChat = (props: SaywhattChatProps) => {
  const { onSendMessage } = props;

  const theme = useTheme();

  const isOpenChat = useValue(isOpenChatAtom);
  const { pageType, productId, variantId, onInit, abTestConfig } = useValue(bridgeAtom);

  const setMessages = useSetValue(messagesAtom);
  const [error, setError] = useAtom(errorAtom);

  const [messageText, setMessageText] = useAtom(messageTextAtom);

  const isShowContent = useRef(isOpenChat);
  const isViewHistorySent = useRef(false);

  const handleSendMessage = useCallback(
    async (msg: MessageType): Promise<void> => {
      const sendResult = await onSendMessage(msg);

      if (sendResult?.error) {
        const updatedMessage = { ...msg, status: SendToModelStatusType.FAILED };

        setMessages((prev) => {
          const filteredMessages = prev.filter((m) => m.clientCorrelationId !== msg.clientCorrelationId);
          return [...filteredMessages, updatedMessage];
        });
      }
    },
    [onSendMessage, setMessages]
  );

  const resendMessage = useCallback(
    (msg: MessageType): void => {
      const updatedMessage = { ...msg, status: SendToModelStatusType.PENDING };

      setMessages((prev) => {
        const filteredMessages = prev.filter((message) => message.clientCorrelationId !== msg.clientCorrelationId);
        return [...filteredMessages, updatedMessage];
      });

      handleSendMessage(updatedMessage);
    },
    [handleSendMessage, setMessages]
  );

  const removeMessage = useCallback(
    (msg: MessageType): void => {
      setMessages((prev) => prev.filter((message) => message.clientCorrelationId !== msg.clientCorrelationId));

      if ([ErrorType.MessageTooLong, ErrorType.MessageNotDelivered].includes(error!)) {
        setError(null);
      }
    },
    [error, setError, setMessages]
  );

  const sendNewMessage = useCallback(
    (val?: string, selectedOptions?: string[]): void => {
      if (!messageText?.length && !val) {
        return;
      }

      const content = messageText || val;

      const { pageType: type, product_id: id } = getProductIdType({
        pageType,
        product_id: productId,
      });

      const newMessage = {
        content: content,
        clientCorrelationId: uuidv4(),
        role: MessageSenderType.USER,
        reason: selectedOptions ? MessageReasonType.SELECTION : MessageReasonType.USER_INPUT,
        status: SendToModelStatusType.PENDING,
        product_id: id,
        variant_id: variantId,
        page_type: type,
        options: selectedOptions,
        clientTimestamp: new Date().getTime(),
      } as MessageType;

      setMessages((prev) => [...prev, newMessage]);
      handleSendMessage(newMessage);
      setMessageText('');
    },
    [handleSendMessage, messageText, pageType, productId, setMessages, setMessageText, variantId]
  );

  const onSendNewMessage = useCallback(
    (event: CustomEvent<{ messageText: string; selectedOptions?: string[] }>) => {
      const { messageText, selectedOptions } = event.detail;
      sendNewMessage(messageText, selectedOptions);
    },
    [sendNewMessage]
  );

  useEvent({ name: AppEvents.SEND_NEW_MESSAGE, handler: onSendNewMessage });

  useEffect(() => {
    if (isOpenChat) {
      addUserScalableNo();
    } else {
      removeUserScalable();
    }

    return () => {
      removeUserScalable();
    };
  }, [isOpenChat]);

  useEffect(() => {
    isViewHistorySent.current = false;
  }, [pageType]);

  useEffect(() => {
    onInit?.();
  }, [onInit]);

  useEffect(() => {
    if (!pageType || isViewHistorySent.current) {
      return;
    }

    viewHistory({
      productId,
      pageType,
      abGroup: abTestConfig?.client?.group ?? null,
      abTestName: abTestConfig?.client?.uuid ?? null,
      mlAbGroup: abTestConfig?.ml?.group ?? null,
      mlAbTestName: abTestConfig?.ml?.uuid ?? null,
    });
    isViewHistorySent.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageType, productId, abTestConfig, isViewHistorySent]);

  if (isOpenChat) {
    isShowContent.current = true;
  }

  return (
    <>
      <FontLoader />
      <Global styles={globalStyles(theme)} />
      <Widget />

      {isShowContent.current && (
        <Suspense fallback={null}>
          <Content sendMessage={sendNewMessage} resendMessage={resendMessage} removeMessage={removeMessage} />
        </Suspense>
      )}
    </>
  );
};
